import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { PhoneNumberApiService } from '../services/phone-number-api.service';
import { PhoneNumberDrawerService } from '../services/phone-number-drawer.service';
import { PhoneNumberResponse } from '../models/phone-number.model';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { HasPermissionDirective } from '../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../shared/directives/disable-without-permission.directive';
import { PHONE_NUMBER_TYPE_LABELS, labelFrom } from '../call-labels';

@Component({
  selector: 'app-phone-number-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent,
    BadgeComponent,
    HasPermissionDirective,
    DisableWithoutPermissionDirective
  ],
  templateUrl: './phone-number-list.component.html'
})
export class PhoneNumberListComponent implements OnInit, OnDestroy {
  items: PhoneNumberResponse[] = [];
  filtered: PhoneNumberResponse[] = [];
  loading = false;
  error = '';

  filtroSearch = '';
  filtroActive: '' | 'true' | 'false' = '';
  filtroType: string = '';

  isSuperAdmin = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly api: PhoneNumberApiService,
    private readonly drawerService: PhoneNumberDrawerService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly toast: ToastService,
    private readonly authService: AuthService
  ) {}

  ngOnInit(): void {
    const user = this.authService.getCurrentUser();
    this.isSuperAdmin = user?.roleName === 'Super Admin';
    if (!this.isSuperAdmin) return;
    this.cargar();
    this.drawerService.saved$.pipe(takeUntil(this.destroy$)).subscribe(() => this.cargar());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  cargar(): void {
    this.loading = true;
    this.error = '';
    this.api.listPhoneNumbers().subscribe({
      next: (list) => {
        this.items = Array.isArray(list) ? list : [];
        this.aplicarFiltros();
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error cargando números telefónicos';
        this.loading = false;
      }
    });
  }

  aplicarFiltros(): void {
    let data = [...this.items];
    if (this.filtroSearch.trim()) {
      const term = this.filtroSearch.trim().toLowerCase();
      data = data.filter(i =>
        i.phoneNumber.toLowerCase().includes(term) ||
        (i.friendlyName || '').toLowerCase().includes(term) ||
        (i.twilioSid || '').toLowerCase().includes(term)
      );
    }
    if (this.filtroActive !== '') {
      const want = this.filtroActive === 'true';
      data = data.filter(i => !!i.isActive === want);
    }
    if (this.filtroType) {
      data = data.filter(i => i.numberType === this.filtroType);
    }
    this.filtered = data;
  }

  buscar(): void {
    this.aplicarFiltros();
  }

  nuevo(): void {
    this.drawerService.open();
  }

  editar(item: PhoneNumberResponse): void {
    this.drawerService.open({ phoneNumberId: item.id });
  }

  marcarDefault(item: PhoneNumberResponse): void {
    if (item.isDefault) return;
    this.api.setDefaultPhoneNumber(item.id).subscribe({
      next: () => {
        this.toast.updated('Número telefónico');
        this.cargar();
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al marcar el número como default' });
      }
    });
  }

  toggleActivo(item: PhoneNumberResponse): void {
    this.api.updatePhoneNumber(item.id, {
      phoneNumber: item.phoneNumber,
      numberType: item.numberType,
      friendlyName: item.friendlyName,
      twilioSid: item.twilioSid,
      agentId: item.agentId,
      teamId: item.teamId,
      setupCost: item.setupCost,
      monthlyCost: item.monthlyCost,
      isActive: !item.isActive,
      isDefault: item.isDefault
    }).subscribe({
      next: () => {
        this.toast.updated('Número telefónico');
        this.cargar();
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al actualizar el número' });
      }
    });
  }

  async eliminar(item: PhoneNumberResponse): Promise<void> {
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar número telefónico',
      message: `¿Eliminar definitivamente "${item.phoneNumber}"?`
    });
    if (!ok) return;
    this.api.deletePhoneNumber(item.id).subscribe({
      next: () => {
        this.cargar();
        this.toast.deleted('Número telefónico');
      },
      error: (err) => {
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al eliminar el número' });
      }
    });
  }

  numberTypeLabel(value: string | null | undefined): string {
    return labelFrom(PHONE_NUMBER_TYPE_LABELS, value);
  }

  trackById = (_: number, item: PhoneNumberResponse) => item.id;
}
